import React from 'react';
import { Copy, ExternalLink, Eye, EyeOff, KeyRound, Link2, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

interface PasswordSectionProps {
  emailAddress?: string | null;
  password: string;
  passwordFailures: number;
  isLocked: boolean;
  showPassword: boolean;
  onPasswordChange: (password: string) => void;
  onTogglePasswordVisibility: () => void;
  onGeneratePassword: () => void;
  onCopyPassword: () => void;
  onSetPassword: () => Promise<void>;
  onSendTempLogin: (options: { resetPassword: boolean; resetTotp: boolean }) => Promise<void>;
  onUnlockAccount: () => Promise<void>;
  settingPassword?: boolean;
  sendingTempLogin?: boolean;
  tempLoginUrl?: string | null;
  tempLoginExpiresAt?: string | null;
}

export const PasswordSection: React.FC<PasswordSectionProps> = ({
  emailAddress,
  password,
  passwordFailures,
  isLocked,
  showPassword,
  onPasswordChange,
  onTogglePasswordVisibility,
  onGeneratePassword,
  onCopyPassword,
  onSetPassword,
  onSendTempLogin,
  onUnlockAccount,
  settingPassword = false,
  sendingTempLogin = false,
  tempLoginUrl,
  tempLoginExpiresAt
}) => {
  const [resetPassword, setResetPassword] = React.useState(false);
  const [resetTotp, setResetTotp] = React.useState(false);
  const [tempLoginCopied, setTempLoginCopied] = React.useState(false);
  const hasEmailAddress = `${emailAddress || ''}`.trim().length > 0;
  const hasPassword = password.trim().length > 0;
  const displayedTempLoginUrl = `${tempLoginUrl || ''}`.trim();
  const hasTempLoginUrl = displayedTempLoginUrl.length > 0;

  const copyTempLoginUrl = async () => {
    if (!displayedTempLoginUrl) return;
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(displayedTempLoginUrl);
    } else if (typeof document !== 'undefined') {
      const copyField = document.createElement('textarea');
      copyField.value = displayedTempLoginUrl;
      copyField.setAttribute('readonly', '');
      copyField.style.position = 'fixed';
      copyField.style.opacity = '0';
      document.body.appendChild(copyField);
      copyField.select();
      document.execCommand('copy');
      document.body.removeChild(copyField);
    }
    setTempLoginCopied(true);
    window.setTimeout(() => setTempLoginCopied(false), 2000);
  };

  return (
    <div className="rounded-md bg-gray-50 p-4">
      <div className="mb-3 flex items-center gap-2">
        <KeyRound className="h-4 w-4 text-gray-500" />
        <h4 className="text-sm font-semibold text-gray-900">Password</h4>
      </div>

      <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
        <div>
          <p className="text-sm font-medium text-gray-900">Temp Login</p>
          <p className="text-xs text-gray-500">{hasEmailAddress ? emailAddress : 'No email address on file'}</p>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={resetPassword}
                onChange={(event) => setResetPassword(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Reset Password
            </label>
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={resetTotp}
                onChange={(event) => setResetTotp(event.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              Reset OTP App
            </label>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onSendTempLogin({ resetPassword, resetTotp })}
          disabled={!hasEmailAddress || (!resetPassword && !resetTotp) || sendingTempLogin}
          className="inline-flex h-9 w-full items-center justify-center gap-2 rounded-md bg-slate-700 px-4 text-sm font-medium text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300 md:w-auto"
        >
          <Link2 className="h-4 w-4" />
          {sendingTempLogin ? 'Sending...' : 'Send Temp Login'}
        </button>
      </div>

      {!hasEmailAddress && (
        <p className="mt-2 text-xs text-red-600">
          This user is missing an email address. Add one before sending a temp login.
        </p>
      )}

      {hasTempLoginUrl && (
        <div className="mt-3 rounded-md border border-gray-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase text-gray-600">Copyable Temp Login Link</p>
              {tempLoginExpiresAt && (
                <p className="text-xs text-gray-500">Expires: {tempLoginExpiresAt}</p>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-1">
              <button
                type="button"
                onClick={copyTempLoginUrl}
                title="Copy temp login link"
                aria-label="Copy temp login link"
                className="inline-flex h-8 items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 text-xs font-medium text-gray-700 hover:bg-gray-100"
              >
                <Copy className="h-4 w-4" />
                {tempLoginCopied ? 'Copied' : 'Copy Link'}
              </button>
              <a
                href={displayedTempLoginUrl}
                target="_blank"
                rel="noreferrer"
                title="Open temp login link"
                aria-label="Open temp login link"
                className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-100"
              >
                <ExternalLink className="h-4 w-4" />
              </a>
            </div>
          </div>
          <input
            readOnly
            value={displayedTempLoginUrl}
            className="mt-2 h-9 w-full rounded-md border border-gray-300 bg-gray-50 px-3 text-xs text-gray-800"
          />
        </div>
      )}

      <div className="mt-4 grid gap-3 border-t border-gray-200 pt-4 lg:grid-cols-[1fr_auto]">
        <div className="min-w-0">
          <label htmlFor="admin-set-user-password" className="mb-1 block text-xs font-semibold uppercase text-gray-600">
            Manual Password
          </label>
          <div className="flex gap-2">
            <input
              id="admin-set-user-password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(event) => onPasswordChange(event.target.value)}
              autoComplete="new-password"
              className="h-9 min-w-0 flex-1 rounded-md border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={onTogglePasswordVisibility}
              disabled={!hasPassword}
              title={showPassword ? 'Hide password' : 'Show password'}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={onGeneratePassword}
              title="Generate password"
              aria-label="Generate password"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-100"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={onCopyPassword}
              disabled={!hasPassword}
              title="Copy password"
              aria-label="Copy password"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Copy className="h-4 w-4" />
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={onSetPassword}
          disabled={!hasPassword || settingPassword}
          className="inline-flex h-9 items-center justify-center gap-2 self-end rounded-md bg-white px-4 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
        >
          <KeyRound className="h-4 w-4" />
          {settingPassword ? 'Setting...' : 'Set Password'}
        </button>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-200 pt-4">
        <span className={cn(
          "inline-flex rounded-full bg-white px-2.5 py-1 text-xs font-semibold ring-1",
          isLocked ? "text-red-800 ring-red-200" : "text-gray-700 ring-gray-200"
        )}>
          Failed attempts: {passwordFailures}
          {isLocked ? ' (locked)' : ''}
        </span>
        <button
          type="button"
          onClick={onUnlockAccount}
          disabled={passwordFailures === 0}
          className="ml-auto inline-flex h-8 items-center rounded-md bg-white px-3 text-xs font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Unlock
        </button>
      </div>
    </div>
  );
};

export default PasswordSection;
